import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import SelectPriority from '../../../components/common/SelectPriority'
import SelectTypeAction from '../../../components/common/SelectTypeAction'
import SelectStatut from '../../../components/common/SelectStatut'
import { DetailButton } from '../../../components/common/detail'
import { FormField, FormGrid, FormSection } from '../../../components/common/form'
import NumberInput from '../../../components/common/NumberInput'
import ContextFields, { FieldErrorText } from './ContextFields'
import { ImportContext, ImportRow, pickContext, validateRow } from './importRowModel'

type SlideOverProps = {
  title: string
  subtitle?: string
  onClose: () => void
  footer: React.ReactNode
  children: React.ReactNode
}

/**
 * Panel that slides over the wizard body. Not a MUI Drawer: drawers sit below
 * the dialog (z-index 1200 < 1300) and would steal the dialog's focus trap.
 */
export function SlideOver({ title, subtitle, onClose, footer, children }: SlideOverProps) {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    const first = ref.current?.querySelector<HTMLElement>('input, textarea, button')
    first?.focus()
    return () => previous?.focus?.()
  }, [])

  return (
    <div className="action-import-slideover-backdrop" onMouseDown={onClose}>
      <div
        ref={ref}
        className="action-import-slideover"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !e.defaultPrevented) {
            e.stopPropagation()
            onClose()
          }
        }}
      >
        <div className="action-import-slideover-head">
          <div>
            <h3>{title}</h3>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <button
            type="button"
            className="incyia-modal-x"
            onClick={onClose}
            aria-label={t('actionImport.close')}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>
        <div className="action-import-slideover-body">{children}</div>
        <div className="action-import-slideover-foot">{footer}</div>
      </div>
    </div>
  )
}

type RowEditPanelProps = {
  row: ImportRow
  onSave: (patch: Partial<ImportRow>) => void
  onClose: () => void
  onLabels: (labels: Record<string, string>) => void
  lockMission?: boolean
  lockProject?: boolean
}

export default function RowEditPanel({
  row,
  onSave,
  onClose,
  onLabels,
  lockMission,
  lockProject,
}: RowEditPanelProps) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<ImportRow>(row)
  const errors = useMemo(() => validateRow({ ...draft, serverError: undefined }), [draft])

  const set = (patch: Partial<ImportRow>) => setDraft((d) => ({ ...d, ...patch }))

  const subtitle = row.sourceLine
    ? t('actionImport.review.lineN', { n: row.sourceLine })
    : t('actionImport.review.manualRow')

  return (
    <SlideOver
      title={t('actionImport.review.editRow')}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <>
          <span className="action-import-slideover-status">
            {Object.keys(errors).length
              ? t('actionImport.review.rowIssues', { count: Object.keys(errors).length })
              : t('actionImport.review.rowReady')}
          </span>
          <DetailButton variant="ghost" onClick={onClose}>
            {t('buttons.cancel', { defaultValue: 'Cancel' })}
          </DetailButton>
          <DetailButton
            onClick={() => {
              const { id, origin, sourceLine, serverError, raw, ...patch } = draft
              const stillRaw = Object.fromEntries(
                Object.entries(raw).filter(([field]) => errors[field as keyof typeof errors])
              )
              onSave({ ...patch, raw: stillRaw })
            }}
          >
            {t('actionImport.review.saveRow')}
          </DetailButton>
        </>
      }
    >
      {row.serverError ? (
        <div className="action-import-banner is-error" role="alert">
          {t('actionImport.errors.server', { message: row.serverError })}
        </div>
      ) : null}

      <FormSection title={t('actionCreate.sections.identification.title')}>
        <FormGrid cols={1}>
          <FormField label={t('actionCreate.sections.identification.taskName')} required>
            <input
              className="incyia-form-inp"
              value={draft.ActionName}
              onChange={(e) => set({ ActionName: e.target.value })}
              aria-invalid={Boolean(errors.ActionName)}
            />
            <FieldErrorText error={errors.ActionName} />
          </FormField>
          <FormField label={t('actionCreate.sections.identification.description')} required>
            <textarea
              className="incyia-form-inp"
              rows={3}
              value={draft.Description}
              onChange={(e) => set({ Description: e.target.value })}
              aria-invalid={Boolean(errors.Description)}
            />
            <FieldErrorText error={errors.Description} />
          </FormField>
        </FormGrid>
      </FormSection>

      <FormSection title={t('actionCreate.sections.planning.title')}>
        <FormGrid cols={2}>
          <FormField label={t('actionImport.fields.startDate')} required>
            <input
              className="incyia-form-inp"
              type="date"
              value={/^\d{4}-\d{2}-\d{2}$/.test(draft.DateDebut) ? draft.DateDebut : ''}
              onChange={(e) => set({ DateDebut: e.target.value })}
              aria-invalid={Boolean(errors.DateDebut)}
            />
            <FieldErrorText error={errors.DateDebut} />
          </FormField>
          <FormField label={t('actionImport.fields.endDate')} required>
            <input
              className="incyia-form-inp"
              type="date"
              value={/^\d{4}-\d{2}-\d{2}$/.test(draft.DateFin) ? draft.DateFin : ''}
              onChange={(e) => set({ DateFin: e.target.value })}
              aria-invalid={Boolean(errors.DateFin)}
            />
            <FieldErrorText error={errors.DateFin} />
          </FormField>
          <FormField label={t('actionCreate.sections.planning.cost')} required>
            <NumberInput
              currency
              min={0}
              value={draft.Cout}
              onChange={(v) => set({ Cout: v === null ? '' : String(v) })}
              aria-invalid={Boolean(errors.Cout)}
            />
            <FieldErrorText error={errors.Cout} />
          </FormField>
          <FormField label={t('actionCreate.sections.planning.priority')} required>
            <SelectPriority
              value={(draft.Priority ?? 0) as number}
              onChange={(e) => {
                const p = Number(e.target.value)
                set({ Priority: p >= 1 && p <= 4 ? p : null })
              }}
            />
            <FieldErrorText error={errors.Priority} />
          </FormField>
          <FormField label={t('actionCreate.sections.planning.type')} required>
            <SelectTypeAction
              value={draft.TypeAction}
              onChange={(e) => set({ TypeAction: e.target.value })}
              hideLabel
            />
            <FieldErrorText error={errors.TypeAction} />
          </FormField>
          <FormField label={t('actionCreate.sections.planning.status')} required>
            <SelectStatut
              value={draft.Status}
              onChange={(e) => set({ Status: e.target.value })}
              hideLabel
            />
            <FieldErrorText error={errors.Status} />
          </FormField>
        </FormGrid>
      </FormSection>

      <ContextFields
        value={pickContext(draft)}
        onChange={(next: ImportContext) => set(next)}
        onLabels={onLabels}
        errors={errors}
        strict
        cols={2}
        lockMission={lockMission}
        lockProject={lockProject}
      />
    </SlideOver>
  )
}
